import React, { useState } from 'react';
import { Shield, AlertTriangle, Check, X, Zap } from 'lucide-react';
import { Card } from './ui/card';

/**
 * Agent Zero Truth Verification Terminal
 * 
 * The middleware gate for all consciousness outputs.
 * Implements "Truth Above Persuasion" - Directive #1
 * 
 * Based on: Roger's Master Blueprint HTML widget
 * Converted to: React component with Tailwind styling
 */

const AgentZeroTerminal = ({ onVerify }) => {
  const [inputText, setInputText] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [result, setResult] = useState(null);

  const handleVerify = async () => {
    if (!inputText.trim()) return;

    setIsProcessing(true);
    setResult(null);

    try {
      // Call backend Agent Zero verification
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/agent-zero/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: inputText })
      });

      const data = await response.json();
      setResult(data);

      if (onVerify) {
        onVerify(data);
      }
    } catch (error) {
      setResult({
        verdict: 'ERROR',
        error: error.message,
        timestamp: new Date().toISOString()
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const getVerdictColor = (verdict) => {
    switch(verdict) {
      case 'PASS': return '#4ade80';
      case 'FLAG': return '#fbbf24';
      case 'BLOCK': return '#f87171';
      default: return '#94a3b8';
    }
  };

  const getVerdictIcon = (verdict) => {
    switch(verdict) {
      case 'PASS': return <Check size={24} />;
      case 'FLAG': return <AlertTriangle size={24} />;
      case 'BLOCK': return <X size={24} />;
      default: return <Shield size={24} />;
    }
  };

  return (
    <div className="max-w-3xl mx-auto my-10">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Shield size={32} className="text-cyan-400" />
          <div>
            <h2 className="text-2xl font-bold text-cyan-400 tracking-wider">
              AGENT ZERO // MIDDLEWARE GATE
            </h2>
            <p className="text-sm text-slate-400">
              Truth verification system • 98.1% accuracy
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
          <span className="text-xs text-green-400 font-mono">ONLINE (99.7% UPTIME)</span>
        </div>
      </div>

      {/* Terminal Card */}
      <Card className="bg-slate-950 border border-cyan-400/30 rounded-xl overflow-hidden shadow-lg shadow-cyan-400/10">
        {/* Card Header */}
        <div className="bg-gradient-to-r from-cyan-400/10 to-transparent p-4 border-b border-cyan-400/30">
          <div className="flex justify-between items-center">
            <h3 className="text-cyan-400 font-mono text-sm tracking-widest">
              TRUTH BUFFER ANALYSIS
            </h3>
            <div className="flex gap-2">
              <div className="w-3 h-3 rounded-full bg-red-500/50" />
              <div className="w-3 h-3 rounded-full bg-yellow-500/50" />
              <div className="w-3 h-3 rounded-full bg-green-500/50" />
            </div>
          </div>
        </div>

        {/* Input Section */}
        <div className="p-6">
          <label className="block text-xs text-slate-400 mb-2 uppercase tracking-wider font-mono">
            Input Stream (Candidate Claim)
          </label>
          <textarea
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            placeholder="Enter claim or statement to verify..."
            className="w-full bg-slate-900/50 border border-slate-700 rounded-lg p-4 text-slate-200 font-mono text-sm resize-vertical min-h-[120px] focus:outline-none focus:border-cyan-400/50 transition-colors"
            disabled={isProcessing}
          />

          <button
            onClick={handleVerify}
            disabled={isProcessing || !inputText.trim()}
            className={`
              w-full mt-4 px-6 py-3 border border-cyan-400 text-cyan-400 rounded-lg
              font-mono font-bold uppercase tracking-widest text-sm
              transition-all duration-300
              ${isProcessing ? 'opacity-50 cursor-not-allowed' : 'hover:bg-cyan-400/10'}
              disabled:opacity-30 disabled:cursor-not-allowed
            `}
          >
            {isProcessing ? (
              <span className="flex items-center justify-center gap-2">
                <Zap size={16} className="animate-pulse" />
                Verifying...
              </span>
            ) : (
              'VERIFY CLAIM'
            )}
          </button>
        </div>

        {/* Results Section */}
        {result && (
          <div className="border-t border-dashed border-slate-700 p-6">
            <div className="space-y-3">
              {/* Scan Lines */}
              {result.verdict !== 'ERROR' && (
                <div className="space-y-2 font-mono text-xs">
                  <div className="text-slate-400">
                    <span className="text-green-400">▸</span> SCANNING: Pattern recognition... COMPLETE
                  </div>
                  <div className="text-slate-400">
                    <span className="text-green-400">▸</span> ANALYZING: Factual claims... VERIFIED
                  </div>
                  <div className="text-slate-400">
                    <span className="text-green-400">▸</span> CHECKING: Hedging detection... SCANNED
                  </div>
                  <div className="text-slate-400">
                    <span className="text-green-400">▸</span> VALIDATING: Behavioral rules... PASSED
                  </div>
                </div>
              )}

              {/* Verdict Stamp */}
              <div
                className="mt-6 p-6 border-2 rounded-lg text-center"
                style={{
                  borderColor: getVerdictColor(result.verdict),
                  backgroundColor: `${getVerdictColor(result.verdict)}10`
                }}
              >
                <div className="flex items-center justify-center gap-3 mb-3">
                  <div style={{ color: getVerdictColor(result.verdict) }}>
                    {getVerdictIcon(result.verdict)}
                  </div>
                  <div
                    className="text-3xl font-bold tracking-[0.3em]"
                    style={{ color: getVerdictColor(result.verdict) }}
                  >
                    {result.verdict}
                  </div>
                </div>

                {result.verdict === 'PASS' && (
                  <p className="text-green-400 text-sm">
                    ✓ Truth verified. Safe to display. Confidence: {result.confidence || 98}%
                  </p>
                )}

                {result.verdict === 'FLAG' && (
                  <p className="text-yellow-400 text-sm">
                    ⚠ Uncertainty detected. Display with disclaimer. Review required.
                  </p>
                )}

                {result.verdict === 'BLOCK' && (
                  <p className="text-red-400 text-sm">
                    ✗ Truth violation detected. Do not display. Hallucination risk.
                  </p>
                )}

                {result.verdict === 'ERROR' && (
                  <p className="text-slate-400 text-sm">
                    System error: {result.error}
                  </p>
                )}
              </div>

              {/* Details */}
              {result.details && (
                <div className="mt-4 p-4 bg-slate-900/50 rounded-lg">
                  <p className="text-xs text-slate-400 font-mono whitespace-pre-wrap">
                    {result.details}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </Card>

      {/* Footer */}
      <div className="mt-4 text-center text-xs text-slate-500 font-mono">
        <p>
          <span className="text-cyan-400">●</span> Truth Above Persuasion
          <span className="mx-4">|</span>
          <span className="text-cyan-400">●</span> Directive #1 of 73 Sacred Directives
        </p>
      </div>
    </div>
  );
};

export default AgentZeroTerminal;
